import React, { useEffect, useState } from 'react';
import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import { 
    Autocomplete, 
    Checkbox, 
    Box, 
    Button, 
    FormControl, 
    FormGroup, 
    IconButton, 
    InputLabel, 
    MenuItem, 
    Select, 
    Snackbar, 
    TextField, 
    Tooltip, 
    Typography, 
    setRef,
    Alert,
    Paper,
    Chip,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import api, { create, get_one, update, delete_one } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { RemoveCircleOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../components/AuthAdminProvider';
import EmpresaSelect from '../../components/EmpresaSelect';
import moment from 'moment';
import CustomTextField from '../../components/CustomTextField';
import { FormControlLabel } from '@mui/material';
import ServiceSelect from '../../components/ServiceSelect';
import DateTime from '../../components/DateTime';

export default function License() {
    const notify = useNotify()
    const auth = useAuth()
    const [formState, setFormState] = useState({
        open: false,
        id: null,
    })
    const [refresh, setRefresh] = useState(false)
    
    React.useEffect(() => {
        document.title = "IPV7 Olimpo - Licenças"
    }, [])

    // Função para verificar uma licença
    const handleVerifyLicense = async (rowData) => {
        const serial = rowData[6]; // Serial está na posição 6
        try {
            // Primeiro busca a licença pelo serial para obter a licença assinada
            const response = await api.get(`/admin/license/serial/${serial}`);
            if (response.data && response.data.signed_license) {
                // Verifica a licença
                const verifyResponse = await api.post('/admin/license/verify', {
                    signed_license: response.data.signed_license
                });
                
                const result = verifyResponse.data;
                if (result.valid) {
                    notify.setNotify({ 
                        open: true, 
                        message: `Licença válida! Expira em ${result.expires_in_days} dias.`, 
                        severity: 'success' 
                    });
                } else {
                    notify.setNotify({ 
                        open: true, 
                        message: `Licença inválida: ${result.error}`, 
                        severity: 'error' 
                    });
                }
            } else {
                notify.setNotify({ 
                    open: true, 
                    message: 'Licença não encontrada ou não possui assinatura digital', 
                    severity: 'warning' 
                });
            }
        } catch (error) {
            notify.setNotify({ 
                open: true, 
                message: 'Erro ao verificar licença', 
                severity: 'error' 
            });
        }
    };

    // Função para baixar arquivo de licença
    const handleDownloadLicense = async (rowData) => {
        const serial = rowData[6]; // Serial está na posição 6
        try {
            const response = await api.get(`/admin/license/serial/${serial}`);
            if (response.data && response.data.license_file) {
                // Cria e baixa o arquivo
                const blob = new Blob([response.data.license_file], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `license.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                notify.setNotify({ 
                    open: true, 
                    message: 'Arquivo de licença baixado com sucesso!', 
                    severity: 'success' 
                });
            }
        } catch (error) {
            notify.setNotify({ 
                open: true, 
                message: 'Erro ao baixar licença', 
                severity: 'error' 
            });
        }
    };

    // Função para regenerar licença
    const handleRegenerateLicense = async (rowData) => {
        const serial = rowData[6];
        try {
            const response = await api.post(`/admin/license/regenerate/${serial}`);
            if (response.data && response.data.license_file) {
                // Cria e baixa o arquivo regenerado
                const blob = new Blob([response.data.license_file], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `license.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                notify.setNotify({ 
                    open: true, 
                    message: 'Licença regenerada e baixada com sucesso!', 
                    severity: 'success' 
                });
            }
        } catch (error) {
            notify.setNotify({ 
                open: true, 
                message: 'Erro ao regenerar licença', 
                severity: 'error' 
            });
        }
    };

    return (
        <>
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => {
                    setFormState({ open: false, id: null })
                    setRefresh(true)
                }}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            />
            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(!refresh)}
                title='Licenças Digitais'
                endpoint='/admin/license'
                options={{
                    customToolbar: () =>
                        <>
                            <Tooltip title="Adicionar Licença">
                                <IconButton 
                                    disabled={!auth.hasPerm('service.create')}
                                    onClick={() => setFormState({ open: true, id: null })} 
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </>,
                    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => ""
                }}
                columns={[
                    {
                        name: 'id',
                        label: 'ID',
                        options: {
                            display: false
                        }
                    },
                    {
                        name: "empresa_name",
                        label: "Empresa",
                    },
                    {
                        name: 'service_description',
                        label: 'Serviço',
                    },
                    {
                        name: 'expire_date',
                        label: 'Data de Expiração',
                        options: {
                            customBodyRender: value => {
                                const expireDate = moment(value);
                                const daysLeft = expireDate.diff(moment(), 'days');
                                const isExpired = daysLeft < 0;
                                const isExpiringSoon = daysLeft <= 30 && daysLeft >= 0;
                                
                                return (
                                    <Box>
                                        <Typography variant="body2">
                                            {expireDate.locale('pt-br').format('DD/MM/YYYY')}
                                        </Typography>
                                        {isExpired && (
                                            <Chip 
                                                label="Expirada" 
                                                color="error" 
                                                size="small" 
                                            />
                                        )}
                                        {isExpiringSoon && (
                                            <Chip 
                                                label={`${daysLeft} dias`} 
                                                color="warning" 
                                                size="small" 
                                            />
                                        )}
                                        {!isExpired && !isExpiringSoon && (
                                            <Chip 
                                                label={`${daysLeft} dias`} 
                                                color="success" 
                                                size="small" 
                                            />
                                        )}
                                    </Box>
                                );
                            }
                        }
                    },
                    {
                        name: 'status',
                        label: 'Status',
                        options: {
                            customBodyRender: value => (
                                <Chip 
                                    label={value ? 'Ativa' : 'Inativa'} 
                                    color={value ? 'success' : 'default'}
                                    icon={value ? <VerifiedIcon /> : undefined}
                                />
                            )
                        }
                    },
                    {
                        name: 'created_at',
                        label: 'Criado em',
                        options: {
                            customBodyRender: value => (
                                <span>{moment(value).locale('pt-br').format('DD/MM/YYYY HH:mm')}</span>
                            ),
                        }
                    },
                    {
                        name: 'serial',
                        label: 'Serial',
                        options: {
                            customBodyRender: value => (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {value ? `${value.substring(0, 8)}...` : 'N/A'}
                                    </Typography>
                                    {value && (
                                        <Tooltip title="Copiar serial completo">
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(value);
                                                    notify.setNotify({ 
                                                        open: true, 
                                                        message: 'Serial copiado!', 
                                                        severity: 'success' 
                                                    });
                                                }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )
                        }
                    },
                    {
                        name: 'actions',
                        label: 'Ações',
                        options: {
                            filter: false,
                            sort: false,
                            customBodyRender: (value, tableMeta, updateValue) => {
                                return (
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Editar Licença">
                                            <IconButton 
                                                disabled={!auth.hasPerm('service.update')}
                                                onClick={() => setFormState({ open: true, id: tableMeta.rowData[0] })}
                                                size="small"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Baixar Arquivo de Licença">
                                            <IconButton 
                                                disabled={!auth.hasPerm('service.get_all')}
                                                onClick={() => handleDownloadLicense(tableMeta.rowData)}
                                                size="small"
                                                color="primary"
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Verificar Licença">
                                            <IconButton 
                                                disabled={!auth.hasPerm('service.get_all')}
                                                onClick={() => handleVerifyLicense(tableMeta.rowData)}
                                                size="small"
                                                color="secondary"
                                            >
                                                <SecurityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Regenerar Licença">
                                            <IconButton 
                                                disabled={!auth.hasPerm('service.update')}
                                                onClick={() => handleRegenerateLicense(tableMeta.rowData)}
                                                size="small"
                                                color="warning"
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )
                            }
                        }
                    }
                ]}
            />
        </>
    )
}

function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar'}) {
    const notify = useNotify()
    const error = useFormError()
    const auth = useAuth()
    const [isLoaded, setIsLoaded] = React.useState(true)
    const modelState = {
        empresa: '',
        service: '',
        expire_date: '',
        status: 1, 
        serial: ''
    }
    const [state, setState] = useState(modelState)
    const [licenseResult, setLicenseResult] = useState(null) // Para armazenar resultado da criação
  
    useEffect(() => {
        setState(modelState)
        setLicenseResult(null)
        if (!open) return
        error.setErrors([])
        if (id) {
            setIsLoaded(false)
            get_one('/admin/license', id).then(res => {
                console.log(res.data.data)
                setState(res.data.data)
                setIsLoaded(true)
            }).catch(e => {
                console.log(e)
                setIsLoaded(true)
            })
        }
    }, [id, open])

    const handleChange = (event) => {
        setState({ ...state, [event.target.name]: event.target.value });
    }

    // Função para baixar o arquivo de licença
    const handleDownloadLicense = () => {
        if (licenseResult && licenseResult.license_file) {
            const blob = new Blob([licenseResult.license_file], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `license.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            notify.setNotify({ 
                open: true, 
                message: 'Arquivo de licença baixado com sucesso!', 
                severity: 'success' 
            });
        }
    }

    // Função para copiar o serial
    const handleCopySerial = () => {
        if (licenseResult && licenseResult.serial) {
            navigator.clipboard.writeText(licenseResult.serial);
            notify.setNotify({ 
                open: true, 
                message: 'Serial copiado para a área de transferência!', 
                severity: 'success' 
            });
        }
    }

    const wrapperSubmit = () => {
        if (id) {
            update('/admin/license', id, state)
                .then(res => {
                    if (res.data.error) {
                        error.setErrors([
                            {field: 'global', msg: res.data.error}
                        ])
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return   
                    }
                    if (res.data.errors) {
                        error.setErrors(res.data.errors)
                        return
                    }
                    
                    // Se retornou uma nova licença assinada, mostra opção de download
                    if (res.data.license_file) {
                        setLicenseResult(res.data);
                        notify.setNotify({ 
                            open: true, 
                            message: 'Licença atualizada! Nova licença assinada gerada.', 
                            severity: 'success' 
                        });
                    } else {
                        handlerSubmit()
                        notify.setNotify({ 
                            open: true, 
                            message: 'Licença atualizada com sucesso', 
                            severity: 'success' 
                        });
                    }
                    error.setErrors([])
                })
                .catch(e => notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        } else {
            create('/admin/license/create', state)
                .then(res => {
                    if (res.data.error) {
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return
                    }
                    if (res.data.errors) {
                        error.setErrors(res.data.errors)
                        return
                    }
                    
                    // Armazena o resultado para mostrar opções de download
                    setLicenseResult(res.data)
                    notify.setNotify({ 
                        open: true, 
                        message: 'Licença criada com sucesso!', 
                        severity: 'success' 
                    })
                    error.setErrors([])
                })
                .catch(e => 
                    notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        }
    }

    return (
        <Box component={"form"}
            onSubmit={wrapperSubmit}
            sx={{ m: 2 }}
            display={'flex'}
            flexWrap={'wrap'}
        >
            <FormDialog
                isLoaded={isLoaded}
                title={
                    <Box display={"flex"} justifyContent={'space-between'} alignItems="center">
                        <Typography variant="h6" component="div">
                            {licenseResult ? 
                                <Box display="flex" alignItems="center" gap={1}>
                                    <SecurityIcon color="success" />
                                    Licença Digital Gerada
                                </Box>
                                : 
                                id ? 'Editar Licença' : 'Criar Nova Licença'
                            } 
                        </Typography>
                        <IconButton 
                            disabled={!auth.hasPerm("service.delete")} 
                            sx={{display: id ? 'block' : 'none'}}
                            onClick={() => {
                                if (confirm('Deseja excluir a licença?')) {
                                    delete_one('/admin/license', id).then(res => {
                                        notify.setNotify({ 
                                            open: true, 
                                            message: 'Licença deletada com sucesso.', 
                                            severity: 'success' 
                                        })
                                        handlerClose()
                                        handlerSubmit()
                                    })
                                }
                            }}
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Box>
                }
                open={open}
                handlerSubmit={wrapperSubmit}
                handlerClose={() => {
                    handlerClose();
                    if (licenseResult) {
                        handlerSubmit(); // Refresh a lista se uma licença foi criada
                    }
                }}
                btnSubmit={licenseResult ? false : btnSubmit}
                btnClose={licenseResult ? 'Fechar' : btnClose}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }} gap={2}>
                    {licenseResult ? 
                        <Card elevation={3}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <VerifiedIcon color="success" />
                                    <Typography variant="h6" color="success.main">
                                        Licença Digital Criada!
                                    </Typography>
                                </Box>
                                
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Importante:</strong> Baixe o arquivo de licença e forneça ao cliente.
                                    </Typography>
                                </Alert>

                                <Box sx={{ 
                                    p: 2, 
                                    bgcolor: 'grey.100', 
                                    borderRadius: 1, 
                                    mb: 2,
                                    fontFamily: 'monospace'
                                }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Serial da Licença:
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {licenseResult.serial}
                                        </Typography>
                                        <Tooltip title="Copiar serial">
                                            <IconButton size="small" onClick={handleCopySerial}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {licenseResult.signed_license && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <SecurityIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            <strong>Licença Assinada Digitalmente</strong><br/>
                                            Esta licença possui assinatura digital HMAC-SHA256 que garante sua autenticidade e impede falsificações.
                                        </Typography>
                                    </Alert>
                                )}
                            </CardContent>
                            
                            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadLicense}
                                    size="large"
                                >
                                    Baixar Arquivo de Licença
                                </Button>
                            </CardActions>
                        </Card>
                    :
                        <>
                            <EmpresaSelect
                                sx={{mt: 1}}
                                value={state.empresa}
                                handler={(e) => setState({...state, empresa: e})}
                                disabled={id}
                            />
                            <ServiceSelect
                                value={state?.service}
                                handler={(e) => setState({ ...state, service: e})} 
                            />
                            <DateTime
                                minDate={ moment() }
                                sx={{width:'100%', m:0}}
                                label="Data de Expiração"
                                handler={(e) => setState({ ...state, expire_date: e })}
                                initValue={state.expire_date}
                            />
                            
                            {state.serial && (
                                <Box sx={{ 
                                    p: 2, 
                                    bgcolor: 'grey.50', 
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.300'
                                }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Serial Atual:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {state.serial}
                                    </Typography>
                                </Box>
                            )}
                            
                            <FormGroup>
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            checked={state?.status}
                                            onChange={(e) => setState({...state, status: e.target.checked ? 1 : 0})}
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>Licença Ativa</span>
                                            {state?.status && <VerifiedIcon color="success" fontSize="small" />}
                                        </Box>
                                    }
                                />
                            </FormGroup>

                            {!id && (
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        <SecurityIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Uma licença digital assinada será gerada automaticamente com proteção HMAC-SHA256.
                                    </Typography>
                                </Alert>
                            )}
                        </>
                    }
                </Box>
            </FormDialog>
        </Box>
    )
}